import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { Party } from '@shared/party/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { FieldType } from '@shared/restaurant/enums';
import { TrColumn } from '@shared/restaurant/models';
import { Location } from '@shared/location/models';
import { AuthUser } from '@shared/signup/models';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { CheckoutStatus } from '@shared/stripe/enums';

import { IParty, OrderByValues } from '@tablesready/common-ui';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

import { NotifyActions } from '../waitlist-notify';
import { MessagesActions } from '../waitlist-messages';
import { WaitlistActionsCallbacks } from '../waitlist-actions';
import { Tables } from '../tr-table/tr-tables.enum';


/**
 * Waitlist party table — a thin wrapper over the generic {@link TrTableComponent}.
 *
 * Builds the column set from the same `waitFields`/`location` settings the legacy
 * waitlist table used (`buildColumns`) and supplies the Party cell markup via
 * projected `*trCell` templates. All table behaviour — sort, pin, resize,
 * reorder, hide, show/hide modal, frozen columns, and internal-scroll `loadMore`
 * windowing — lives in the shell.
 */
@Component({
  selector: 'tablesready-party-table',
  templateUrl: './party-table.component.html',
  styleUrls: ['./party-table.component.scss'],
  standalone: false,
})
export class PartyTableComponent implements OnChanges {
  @Input() parties: Party[] = [];
  @Input() location!: Location;
  @Input() user!: AuthUser;
  /** Resolved wait-field flags (position_in_line, size, note_column, quoted_time, arrival_time, wait_time). */
  @Input() waitFields: any;
  @Input() services: Service[] = [];
  @Input() resources: Resource[] = [];
  @Input() servicesEnabled = false;
  @Input() resourcesEnabled = false;
  @Input() sortBy = 'quoted_time';
  @Input() sortReverse = false;
  @Input() now = 0;
  @Input() displayCompact = false;
  @Input() showCustomFieldsData = false;
  @Input() isMobile = false;
  @Input() isHandset = false;
  @Input() notifyActions?: NotifyActions<Party>;
  @Input() messagesActions?: MessagesActions<Party>;
  @Input() actionsCallbacks?: WaitlistActionsCallbacks<IParty>;
  /** True when some visible row shows the Text button — reserves its slot on the others. */
  @Input() anyPartyNotifiable = false;
  @Input() highlightedPartyId: string | null = null;
  /** Row id emitted as the `<tr>` id — the scroll-to-row anchor. */
  @Input() rowId?: (row: Party) => string;
  /** Rows to collapse at once, e.g. a party just served, before the store update removes it. */
  @Input() rowHidden?: (row: Party) => boolean;
  /** Height of the scroll viewport. Set to `'flex'` to fill the parent. */
  @Input() scrollHeight = '60vh';
  /** Show drag handles / allow resize + auto-fit. */
  @Input() resizableColumns = true;
  /** True when the host still has rows beyond the current `parties` slice. */
  @Input() hasMore = false;
  @Input() table = Tables.waitlist;
  @Input() updateColumns: (columns: TrColumn[]) => void;

  @Output() orderByChange = new EventEmitter<OrderByValues>();
  /** Emitted when the internal scroll nears the bottom and more rows should be windowed in. */
  @Output() loadMore = new EventEmitter<void>();

  /** All available columns (respecting waitFields/settings), including hidden ones. */
  columns: TrColumn[] = [];
  columnsToSave: TrColumn[] | null = null;

  /**
   * What the show/hide modal explains about each column, in the words the settings
   * page used before these toggles moved into the table. Built alongside the
   * columns, so it follows the location's own label for a guest.
   */
  columnHints: Record<string, string> = {};

  readonly partyTypes = PartyTypes;
  readonly partySources = PartySource;
  readonly fieldTypes = FieldType;

  constructor(@Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.location) {
      // Re-read on every location change, not once at init: "Save as Location
      // Default" writes the layout to the location and comes back as a
      // `locationUpdated` event, so a snapshot taken at page load would leave
      // "Reset to Location Default" restoring the layout this page opened with.
      this.columnsToSave = this.location?.tablesSettings?.[this.table] || null;
    }
    // Rebuild columns only when their structure could have changed.
    if (
      changes.waitFields || changes.location || changes.servicesEnabled ||
      changes.resourcesEnabled || changes.showCustomFieldsData || changes.user
    ) {
      this.buildColumns();
    }
  }

  trackById = (_: number, item: Party): string => item._id;

  isArray(val: unknown): boolean {
    return val instanceof Array;
  }

  /** Drop the standalone word "time" from a header label to save space (e.g. "quoted time" → "quoted"). */
  shortenTimeLabel(label: string): string {
    return (label || '').replace(/\btime\b/gi, '').replace(/\s+/g, ' ').trim();
  }

  onOrderBy(value: OrderByValues): void {
    this.orderByChange.emit(value);
  }

  // ----- column model ------------------------------------------------------

  /**
   * The column set this table can show.
   *
   * `#`, notes, arrival and wait are always built, hidden or not: they change
   * nothing but this table, so the show/hide modal — not a settings page — is
   * where they belong. Their `waitFields` flags survive only as the *default*
   * visibility for a device that has never touched the layout, which keeps each
   * location looking the way it did before the settings toggles were removed.
   * Everything else here is still gated: those fields also drive the add/edit
   * forms and the data.
   */
  private buildColumns(): void {
    const tableSettings = this.location.tablesSettings[this.table] || null;
    const labels = this.location?.displayOptions?.labels;
    const next: TrColumn[] = [];

    this.columnHints = {
      pos: `Add a column with the position in line for each ${labels?.customer || 'party'}.`,
      note: 'Instead of listing notes with the name, make them their own column.',
      arrival: 'Show the arrival time as a column.',
      wait: 'Show how long someone has been waiting as a column.',
    };

    // '#' rather than 'Pos' — the column only ever holds a 1-2 digit index. Out of
    // the table (the show/hide modal) that symbol means nothing, hence `label`.
    next.push({
      key: 'pos', header: '#', label: 'position in line', listLabel: false,
      hidden: !this.waitFields?.position_in_line,
      headerClass: 'text-center', cellClass: 'text-center',
    });
    next.push({
      key: 'name',
      header: labels?.customer || 'customer',
      sortKey: 'name',
      // The guest's name needs no "Customer:" prefix in the list layout.
      listLabel: false,
    });
    if (this.waitFields?.size) {
      next.push({
        key: 'size', header: 'size', sortKey: 'size',
        headerClass: 'text-center', cellClass: 'text-center',
      });
    }
    next.push({
      key: 'note', header: 'Notes', sortKey: 'note',
      hidden: !this.waitFields?.note_column,
    });
    if (this.servicesEnabled && this.location?.serviceSettings?.showOnPages) {
      next.push({ key: 'service', header: this.location?.serviceSettings?.label || 'service', sortKey: 'service' });
    }
    if (this.resourcesEnabled && this.location?.resourceSettings?.showOnPages) {
      next.push({ key: 'resource', header: this.location?.resourceSettings?.label || 'resource', sortKey: 'resource' });
    }
    if (this.waitFields?.quoted_time) {
      next.push({
        key: 'quoted',
        // The header drops "time" to save a column's width; the modal has room.
        header: this.shortenTimeLabel(labels?.quoted || 'quoted time'),
        label: labels?.quoted || 'quoted time',
        sortKey: 'quoted_time',
      });
    }
    next.push({
      key: 'arrival', header: 'arrival', label: 'arrival time', sortKey: 'checkin_time',
      hidden: !this.waitFields?.arrival_time,
    });
    next.push({
      key: 'wait', header: 'wait', label: 'wait time', sortKey: 'wait_time',
      hidden: !this.waitFields?.wait_time,
    });
    if (this.showCustomFieldsData && this.user?.location?.partyFields) {
      for (const field of this.user.location.partyFields) {
        if (field.enabled && field.visibility?.private?.view) {
          next.push({
            key: `cf_${field.id}`,
            header: field.name,
            templateKey: 'custom',
            customFieldId: field.id,
            customFieldTel: field.type === FieldType.tel,
            customFieldText: field.type === FieldType.text,
          });
        }
      }
    }
    // align-top: the cell fills the row's height from the top and clips the oldest
    // messages, so it must not be vertically centred.
    next.push({ key: 'messages', header: 'Messages', cellClass: 'align-top', listLabel: false });
    next.push({ key: 'actions', header: 'Actions', listLabel: false });

    // Structure only — the table shell re-applies the device's saved
    // order/visibility/pin/width over this set on every rebuild.
    this.columns = next;
  }

  // ----- cell helpers ------------------------------------------------------

  unpaid(party: Party): boolean {
    return party.stripeData?.status === CheckoutStatus.inProgress
      && (party.created_time + this.appVariables.UNPAID_PARTY_LIFETIME * 1000) > Date.now();
  }

  paid(party: Party): boolean {
    return party.stripeData?.status === CheckoutStatus.paid;
  }

  getRemainingSecondsToPay(partyCreatedTime: number): number {
    return partyCreatedTime + (this.appVariables.UNPAID_PARTY_LIFETIME * 1000);
  }

  displayCustomFieldCell(party: Party, customFieldId?: string): boolean {
    if (!customFieldId) {
      return false;
    }
    if (this.isMobile) {
      return party.customFields[customFieldId] !== null && party.customFields[customFieldId] !== undefined;
    }

    return true;
  }
}
